import React, { useState } from 'react';

export const ScorecardMini = ({ holesData, holeScores, currentHole, onHoleClick, expandable = false }) => {
  const [expandedHole, setExpandedHole] = useState(null);

  const getScoreStyle = (score) => {
    if (!score) return { bg: '', text: 'text-muted-foreground', border: 'border-border/30' };
    const diff = score.strokes - score.par;
    if (diff <= -2) return { bg: 'bg-yellow-500', text: 'text-white', border: 'border-yellow-600', shape: 'double-circle' };
    if (diff === -1) return { bg: 'bg-red-500', text: 'text-white', border: 'border-red-600', shape: 'circle' };
    if (diff === 0) return { bg: 'bg-transparent', text: 'text-foreground', border: 'border-transparent', shape: 'none' };
    if (diff === 1) return { bg: 'bg-transparent', text: 'text-blue-500', border: 'border-blue-500', shape: 'square' };
    return { bg: 'bg-transparent', text: 'text-blue-600', border: 'border-blue-600', shape: 'double-square' };
  };

  const handleHoleClick = (n) => {
    if (expandable) {
      setExpandedHole(expandedHole === n ? null : n);
    } else {
      onHoleClick(n);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border/50 overflow-hidden" data-testid="scorecard-mini">
      <div className="flex border-b border-border/50">
        <div className="w-12 py-1.5 text-center text-[10px] font-bold text-muted-foreground bg-muted/50">HOLE</div>
        {holesData.map((_, i) => {
          const n = i + 1;
          const isCurrent = n === currentHole;
          return (
            <button key={n} onClick={() => handleHoleClick(n)}
              className={`flex-1 py-1.5 text-center text-[10px] font-bold transition-colors ${isCurrent ? 'bg-primary text-primary-foreground' : expandedHole === n ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/50'}`}
              data-testid={`scorecard-hole-${n}`}
            >{n}</button>
          );
        })}
        <div className="w-12 py-1.5 text-center text-[10px] font-bold text-muted-foreground bg-muted/50">TOT</div>
      </div>
      <div className="flex border-b border-border/30">
        <div className="w-12 py-1 text-center text-[10px] text-muted-foreground bg-muted/30">PAR</div>
        {holesData.map((h, i) => (
          <div key={i} className="flex-1 py-1 text-center text-[10px] text-muted-foreground">{h.par}</div>
        ))}
        <div className="w-12 py-1 text-center text-[10px] font-semibold text-muted-foreground bg-muted/30">
          {holesData.reduce((s, h) => s + h.par, 0)}
        </div>
      </div>
      <div className="flex">
        <div className="w-12 py-1.5 text-center text-[10px] font-bold text-foreground bg-muted/30">YOU</div>
        {holesData.map((_, i) => {
          const n = i + 1;
          const score = holeScores.find(s => s.hole_number === n);
          const style = getScoreStyle(score);
          return (
            <button key={n} onClick={() => handleHoleClick(n)} className="flex-1 py-1 flex items-center justify-center">
              <span className={`w-6 h-6 flex items-center justify-center text-[11px] font-bold ${style.text} ${
                style.shape === 'circle' ? 'rounded-full border-2 ' + style.border :
                style.shape === 'double-circle' ? 'rounded-full border-2 ring-2 ring-offset-1 ' + style.border + ' ring-yellow-500' :
                style.shape === 'square' ? 'rounded-sm border-2 ' + style.border :
                style.shape === 'double-square' ? 'rounded-sm border-2 ring-2 ring-offset-1 ' + style.border + ' ring-blue-600' : ''
              }`}>
                {score ? score.strokes : '-'}
              </span>
            </button>
          );
        })}
        <div className="w-12 py-1.5 text-center text-xs font-bold text-foreground bg-muted/30">
          {holeScores.reduce((s, h) => s + h.strokes, 0) || '-'}
        </div>
      </div>
      {expandedHole && (() => {
        const score = holeScores.find(s => s.hole_number === expandedHole);
        if (!score) return null;
        const pd = score.penalty_details || {};
        const bd = score.bunker_details || {};
        return (
          <div className="border-t border-border/50 p-3 bg-muted/20 animate-in slide-in-from-top-2" data-testid={`expanded-hole-${expandedHole}`}>
            <div className="text-xs font-bold text-foreground mb-2">Hole {expandedHole} Details</div>
            <div className="grid grid-cols-4 gap-2 text-[10px]">
              <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground text-sm">{score.strokes}</div><div className="text-muted-foreground">Score</div></div>
              <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground text-sm">{score.putts}</div><div className="text-muted-foreground">Putts</div></div>
              <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground text-sm capitalize">{score.par >= 4 ? (score.drive_accuracy || '-') : 'N/A'}</div><div className="text-muted-foreground">Tee Shot</div></div>
              <div className="bg-background rounded-lg p-2 text-center"><div className="font-bold text-foreground text-sm">{score.tee_club || '-'}</div><div className="text-muted-foreground">Club</div></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="bg-background rounded-lg p-2 text-[10px]">
                <div className="font-bold text-foreground mb-1">Penalties ({score.penalties || 0})</div>
                {(score.penalties || 0) > 0 ? (
                  <div className="flex gap-2 text-muted-foreground">
                    {pd.h2o > 0 && <span>H2O: {pd.h2o}</span>}
                    {pd.ob > 0 && <span>OB: {pd.ob}</span>}
                    {pd.dropped > 0 && <span>Drop: {pd.dropped}</span>}
                    {!pd.h2o && !pd.ob && !pd.dropped && <span>{score.penalties}</span>}
                  </div>
                ) : <div className="text-muted-foreground">None</div>}
              </div>
              <div className="bg-background rounded-lg p-2 text-[10px]">
                <div className="font-bold text-foreground mb-1">Bunkers ({score.bunkers || 0})</div>
                {(score.bunkers || 0) > 0 ? (
                  <div className="flex gap-2 text-muted-foreground">
                    {bd.greenside > 0 && <span>GS: {bd.greenside}</span>}
                    {bd.fairway > 0 && <span>FW: {bd.fairway}</span>}
                    {!bd.greenside && !bd.fairway && <span>{score.bunkers}</span>}
                  </div>
                ) : <div className="text-muted-foreground">None</div>}
              </div>
            </div>
            {score.first_putt_distance && (
              <div className="mt-2 text-[10px] text-muted-foreground">1st Putt: <span className="font-semibold text-foreground">{score.first_putt_distance}</span></div>
            )}
          </div>
        );
      })()}
    </div>
  );
};
